/**
 * Landing v2 — public marketing surface for AgenteLead Pro.
 *
 * Changes from v1 (user feedback):
 *   • FORCED light theme — no dark-mode bleed. Every color is explicit.
 *   • High-contrast palette: ivory `#FBF9F4` bg, charcoal `#0F172A` text,
 *     gold `#84CC16` CTAs, emerald `#059669` for progress/status.
 *   • NO vendor leaking — "Apify" removed from copy. Features talk about
 *     capabilities, not the underlying plumbing.
 *   • Integration logos strip (Google Maps, Instagram, LinkedIn Sales,
 *     YouTube, Yelp, Reddit).
 *   • Business-type photo gallery (Roofing / HVAC / Solar / Plumbing / GC /
 *     Electrical) — gives tangible context to "who we find".
 *   • New sections: Case Study (5 tabs) + Solutions (3 personas).
 *   • Trilingual: PT / EN / ES. Language state persisted.
 *
 * Route: /landing (public). Protected routes live elsewhere.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { useABVariant } from "../hooks/useABVariant";
import IntegrationsLiveCount from "../components/IntegrationsLiveCount";
import {
  ArrowRight, Check, ChevronDown, MapPin, Sparkles, Workflow, Send,
  Briefcase, Users, Building2, Headphones, Globe, Search, Zap, Loader2,
  Star, PlayCircle, Inbox, BarChart3, HardHat,
  Package as PackageIcon, Building, Lock as LockIcon, Clock, FileSpreadsheet,
  TrendingDown, Rocket, X as XIcon, Menu, ArrowLeft, Link2 as LinkIcon,
} from "lucide-react";
import COPY from "./landing/copy";
import {
  LOGO_URL, BRAND, INTEGRATION_LOGOS, BUSINESS_GALLERY, AVATARS, LANGS, fadeUp,
} from "./landing/constants";
import {
  LangSwitcher, SectionHeader, BentoCard, FaqRow, RoiCalculator,
  PricingSection, FooterCol, FloatingProductMontage,
  ExcelVsAgenteLeadDramatic, ScrollytellingRail, CompareMobileStack,
} from "./landing/sections";
import StrategicComparison from "./landing/StrategicComparison";
import B2CRadarSection from "./landing/B2CRadarSection";
import WinningStrategySection from "./landing/WinningStrategySection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


export default function Landing() {
  // Iter320 · Track whether the visitor arrived with NO explicit language
  // preference. Set inside the useState initializer BEFORE any effect has
  // a chance to run persistence side-effects. This is the guard the IP-
  // detect effect consults so it never races with the localStorage.setItem
  // in the persistence effect below.
  const langCameFromDefaultRef = useRef(false);
  const [lang, setLang] = useState(() => {
    try {
      const saved = localStorage.getItem("alp.lang");
      if (["pt", "en", "es"].includes(saved)) return saved;
      langCameFromDefaultRef.current = true;
      // Iter314 · auto-detect from browser on first visit so PT/ES visitors
      // land on the site already in their language before creating an account.
      const nav = (navigator.language || navigator.userLanguage || "en").toLowerCase();
      if (nav.startsWith("pt")) return "pt";
      if (nav.startsWith("es")) return "es";
      return "en";
    } catch { return "en"; }
  });
  const [openFaq, setOpenFaq] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "" });
  const [solTab, setSolTab] = useState("gc");
  const [caseTab, setCaseTab] = useState("all");
  // Iter279 · Toolkit lightbox — clicking any tile stores the tile
  // shape here so `<ToolkitLightbox>` can render a full-screen preview.
  const [toolkitPreview, setToolkitPreview] = useState(null);

  // Force light theme on the landing root — dark-mode CSS had been
  // overriding bg-white, which made the page unreadable (dark bg + dark
  // blue text). Scoped override: only while this component is mounted.
  useEffect(() => {
    const root = document.documentElement;
    const prev = root.getAttribute("data-theme");
    root.setAttribute("data-theme", "dark");
    return () => { if (prev !== null) root.setAttribute("data-theme", prev); else root.removeAttribute("data-theme"); };
  }, []);
  useEffect(() => { try { localStorage.setItem("alp.lang", lang); } catch (_e) { /* ignore */ } }, [lang]);

  // Iter319 · IP-based language fallback for FIRST-time visitors only.
  // Fires when localStorage was empty AND the navigator locale didn't
  // decide (so we're still on the default `en`). Uses the same public
  // `/api/geo/lang` endpoint that authenticated pages consume. Never
  // overrides an explicit choice — once the visitor picks a flag we
  // persist it in localStorage and this effect goes back to no-op.
  //
  // Iter320 · When the flip happens, we also raise a small dismissable
  // country hint banner so the visitor knows the switch was automatic and
  // can jump back to EN with a single click. The hint payload is written
  // to localStorage (`alp.geo_hint`) so it survives StrictMode double-
  // mount + browser reloads until the visitor either dismisses it or
  // manually swaps the language. `alp.geo_hint_shown` records that the
  // banner has been resolved so it never comes back.
  const langAutoBumped = useRef(false);
  const [geoHint, setGeoHint] = useState(() => {
    try {
      if (localStorage.getItem("alp.geo_hint_shown")) return null;
      const raw = localStorage.getItem("alp.geo_hint");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  useEffect(() => {
    if (langAutoBumped.current) return;
    // Iter320 · The persistence effect above may have already written the
    // default `en` to localStorage before this effect runs, so we can't
    // rely on `localStorage.getItem("alp.lang")` here. `langCameFromDefaultRef`
    // captures the "empty localStorage on first mount" signal set at
    // useState init time, before any side-effect had a chance to run.
    if (!langCameFromDefaultRef.current) return;
    if (lang !== "en") return; // navigator already picked pt/es
    let hintDone;
    try { hintDone = localStorage.getItem("alp.geo_hint_shown"); }
    catch { hintDone = null; }
    langAutoBumped.current = true;
    axios.get(`${API}/geo/lang`, { timeout: 4000 })
      .then((r) => {
        const l = r?.data?.lang;
        const country = r?.data?.country;
        if ((l === "pt" || l === "es") && l !== lang) {
          setLang(l);
          if (!hintDone && country) {
            const hint = { country, targetLang: l, prevLang: "en" };
            setGeoHint(hint);
            try { localStorage.setItem("alp.geo_hint", JSON.stringify(hint)); } catch { /* ignore */ }
          }
        }
      })
      .catch(() => { /* silent — landing stays on EN */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismissGeoHint = () => {
    setGeoHint(null);
    try {
      localStorage.setItem("alp.geo_hint_shown", "yes");
      localStorage.removeItem("alp.geo_hint");
    } catch { /* ignore */ }
  };
  const revertGeoHint = () => {
    if (!geoHint) return;
    setLang(geoHint.prevLang);
    dismissGeoHint();
  };

  const c = useMemo(() => COPY[lang], [lang]);

  // Iter287 · Flatten toolkit items once so we can (a) power lightbox
  // prev/next, (b) resolve `?tool=<slug>` deep-links on mount.
  const allToolkitItems = useMemo(
    () => c.toolkit.groups.flatMap((g) =>
      g.items.map((it) => ({ ...it, groupLabel: g.label, groupColor: g.color,
        slug: it.t.toLowerCase().replace(/[^a-z0-9]/g, "-").slice(0, 30) }))),
    [c.toolkit.groups]);

  // Iter287 · Resolve `?tool=<slug>` on first mount → auto-open the
  // matching lightbox so shared links land on the previewed tile.
  const _initialTool = useRef(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const slug = params.get("tool");
    if (!slug) { _initialTool.current = true; return; }
    const match = allToolkitItems.find((it) => it.slug === slug);
    if (match) setToolkitPreview(match);
    _initialTool.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Iter287 · Deep-link the lightbox — push `?tool=<slug>` when opened
  // and clear it on close. Enables shareable direct-preview links like
  // `/#toolkit?tool=lead-discovery` or `/?tool=vehicle-security`.
  useEffect(() => {
    // Skip the very first sync so the mount effect above can hydrate
    // toolkitPreview from `?tool=` before we clobber the URL.
    if (!_initialTool.current) return;
    const url = new URL(window.location.href);
    if (toolkitPreview?.slug) {
      if (url.searchParams.get("tool") !== toolkitPreview.slug) {
        url.searchParams.set("tool", toolkitPreview.slug);
        window.history.replaceState({}, "", url.toString());
      }
    } else if (url.searchParams.has("tool")) {
      url.searchParams.delete("tool");
      window.history.replaceState({}, "", url.toString());
    }
  }, [toolkitPreview]);

  // Iter55 — A/B test hero primary CTA. Variant A = current trial copy,
  // variant B = curiosity-driven "see it find leads in 60s" framing.
  const heroCtaCopies = useMemo(() => ({
    A: c.hero.cta1,
    B: lang === "pt" ? "Veja achar leads em 60s"
       : lang === "es" ? "Velo encontrar leads en 60s"
       : "See it find leads in 60s",
  }), [c.hero.cta1, lang]);
  const heroAB = useABVariant("hero-primary", heroCtaCopies, { lang, page: "landing" });

  // Iter57 — mobile menu state + scrollytelling progress within the hero.
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      // 0..1 progress across the first viewport (~hero height).
      const max = Math.max(window.innerHeight * 0.9, 1);
      setScrollProgress(Math.max(0, Math.min(1, window.scrollY / max)));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  // Close mobile menu on route hash change
  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const close = () => setMobileMenuOpen(false);
    window.addEventListener("hashchange", close);
    return () => window.removeEventListener("hashchange", close);
  }, [mobileMenuOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault(); setFormError(""); setSubmitting(true);
    try {
      await axios.post(`${API}/marketing/demo-request`, {
        name: form.name, company: form.company, email: form.email,
        phone: form.phone, lang, source: "landing_page",
      });
      setSubmitted(true);
      setForm({ name: "", company: "", email: "", phone: "" });
      setTimeout(() => setSubmitted(false), 8000);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setFormError(typeof detail === "string" ? detail
        : (lang === "pt" ? "Falha ao enviar. Tente novamente."
          : lang === "es" ? "Error al enviar. Intenta de nuevo."
          : "Failed to send. Try again."));
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="landing-page"
      className="min-h-screen antialiased font-body selection:bg-[#84CC16]/40 pb-[68px] lg:pb-0"
      style={{ background: "#0F172A", color: "#FFFFFF" }}>
      {/* Iter320 · Country hint banner — appears only when the language was
          auto-flipped via IP geolocation, so the visitor knows the switch
          happened automatically and can jump back to EN with one click. */}
      {geoHint && (
        <GeoHintBanner hint={geoHint}
                       onKeep={dismissGeoHint}
                       onRevert={revertGeoHint} />
      )}
      {/* === Sticky glass nav === */}
      <header data-testid="nav-bar"
        className="sticky top-0 z-50 backdrop-blur-xl border-b"
        style={{ backgroundColor: "rgba(15,8,32,0.78)", borderColor: "rgba(132,204,22,0.2)" }}>
        <div className="max-w-7xl mx-auto px-3 sm:px-5 lg:px-10 h-14 sm:h-16 flex items-center justify-between gap-1.5 sm:gap-3 lg:gap-6">
          <a href="#top" className="flex items-center gap-1.5 sm:gap-2.5 min-w-0 shrink" data-testid="landing-brand">
            <img src={LOGO_URL} alt="AgenteLead Pro" className="h-8 w-8 sm:h-10 sm:w-10 object-contain shrink-0" />
            <span className="font-display text-[14px] sm:text-[19px] font-extrabold tracking-tight leading-none whitespace-nowrap" style={{ color: "#FFFFFF" }}>
              AgenteLead<span style={{ color: "#84CC16" }}>.Pro</span>
            </span>
          </a>
          <nav className="hidden lg:flex items-center gap-8 text-[13.5px] font-medium" style={{ color: "#A1A1AA" }}>
            <a href="#product" className="hover:text-white transition-colors" data-testid="landing-nav-product">{c.nav.product}</a>
            <a href="#toolkit" className="hover:text-white transition-colors" data-testid="landing-nav-toolkit">{c.toolkit.eyebrow}</a>
            <a href="#solutions" className="hover:text-white transition-colors" data-testid="landing-nav-solutions">{c.nav.solutions}</a>
            <a href="#cases" className="hover:text-white transition-colors" data-testid="landing-nav-cases">{c.nav.cases}</a>
            <a href="#pricing" className="hover:text-white transition-colors" data-testid="landing-nav-pricing">Pricing</a>
            <a href="#compare" className="hover:text-white transition-colors" data-testid="landing-nav-compare">{c.nav.compare}</a>
            <a href="#faq" className="hover:text-white transition-colors" data-testid="landing-nav-faq">{c.nav.faq}</a>
            <Link to="/blog" className="hover:text-white transition-colors" data-testid="landing-nav-blog">Blog</Link>
          </nav>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            {/* Iter271 · LangSwitcher moved into the drawer on <md to give room
                 for the TRIAL CTA. Desktop keeps it inline. */}
            <div className="hidden md:block">
              <LangSwitcher lang={lang} setLang={setLang} />
            </div>
            {/* Mobile hamburger — opens lateral drawer with lang, nav and CTAs. */}
            <button type="button"
              onClick={() => setMobileMenuOpen((v) => !v)}
              data-testid="landing-mobile-menu-toggle"
              aria-expanded={mobileMenuOpen} aria-label="Open menu"
              className="lg:hidden inline-flex items-center justify-center w-9 h-9 rounded-full border border-white/15 hover:bg-white/5 transition-colors"
              style={{ color: "#FFFFFF" }}>
              {mobileMenuOpen ? <XIcon className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
            {/* Mobile: lock icon → /login. Desktop: full "Sign in" text. */}
            <Link to="/login"
              aria-label={c.nav.login}
              data-testid="landing-login-link"
              className="hidden sm:inline-flex items-center justify-center w-9 h-9 sm:w-auto sm:h-auto sm:px-3 sm:py-1.5 rounded-full sm:rounded-md border border-white/15 sm:border-0 hover:bg-white/5 sm:hover:bg-transparent transition-colors"
              style={{ color: "#A1A1AA" }}>
              <LockIcon className="w-4 h-4 sm:hidden" />
              <span className="hidden sm:inline text-[13px] font-semibold hover:text-white">{c.nav.login}</span>
            </Link>
            <Link to="/signup"
              className="inline-flex items-center gap-1 sm:gap-1.5 text-[#0F172A] text-[11px] sm:text-[13px] font-extrabold uppercase tracking-tight px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-md transition-all hover:-translate-y-0.5 whitespace-nowrap"
              style={{ backgroundColor: "#84CC16", boxShadow: "0 8px 30px -10px rgba(132,204,22,0.55)" }}
              data-testid="landing-header-cta">
              <span className="sm:hidden">Trial</span>
              <span className="hidden sm:inline">{c.nav.demo}</span>
              <ArrowRight className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            </Link>
          </div>
        </div>

        {/* Iter271 · Lateral mobile drawer (right-side sheet). Slides in
             from the right, full-height, with LangSwitcher inline, nav
             list, login row and a duplicated TRIAL CTA at the bottom. */}
        {mobileMenuOpen && (
          <>
            <div className="lg:hidden fixed inset-0 z-40"
                 style={{ background: "rgba(2,6,23,0.7)", backdropFilter: "blur(4px)" }}
                 onClick={() => setMobileMenuOpen(false)} />
            <aside data-testid="landing-mobile-menu"
              className="lg:hidden fixed top-0 right-0 z-50 h-full w-[86%] max-w-[340px] flex flex-col shadow-2xl border-l"
              style={{ background: "rgba(15,8,32,0.98)", borderColor: "rgba(132,204,22,0.2)" }}>
              <div className="flex items-center justify-between px-5 py-3.5 border-b"
                   style={{ borderColor: "rgba(132,204,22,0.15)" }}>
                <div className="flex items-center gap-2">
                  <img src={LOGO_URL} alt="" className="h-8 w-8 object-contain" />
                  <span className="font-display text-[14px] font-extrabold text-white">
                    AgenteLead<span style={{ color: "#84CC16" }}>.Pro</span>
                  </span>
                </div>
                <button onClick={() => setMobileMenuOpen(false)}
                        data-testid="landing-mobile-menu-close"
                        aria-label="Close menu"
                        className="w-11 h-11 -mr-2 rounded-full hover:bg-white/10 text-slate-300 inline-flex items-center justify-center touch-target">
                  <XIcon className="w-5 h-5" />
                </button>
              </div>
              <nav className="flex-1 overflow-y-auto px-5 py-4 flex flex-col">
                {/* Iter271 · Language switcher lives inside the drawer on mobile */}
                <div className="pb-3 mb-3 border-b" style={{ borderColor: "rgba(132,204,22,0.12)" }}>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Idioma</div>
                  <LangSwitcher lang={lang} setLang={setLang} dark />
                </div>
                {[
                  { href: "#product", label: c.nav.product, key: "product" },
                  { href: "#toolkit", label: c.toolkit.eyebrow, key: "toolkit" },
                  { href: "#solutions", label: c.nav.solutions, key: "solutions" },
                  { href: "#cases", label: c.nav.cases, key: "cases" },
                  { href: "#pricing", label: "Pricing", key: "pricing" },
                  { href: "#compare", label: c.nav.compare, key: "compare" },
                  { href: "#faq", label: c.nav.faq, key: "faq" },
                ].map((it) => (
                  <a key={it.key} href={it.href}
                    data-testid={`landing-mobile-nav-${it.key}`}
                    onClick={() => setMobileMenuOpen(false)}
                    className="py-3.5 text-[15px] font-semibold border-b last:border-b-0 touch-target"
                    style={{ color: "rgba(255,255,255,0.9)", borderColor: "rgba(132,204,22,0.12)" }}>
                    {it.label}
                  </a>
                ))}
                <Link to="/blog"
                  data-testid="landing-mobile-nav-blog"
                  onClick={() => setMobileMenuOpen(false)}
                  className="py-3.5 text-[15px] font-semibold border-b touch-target"
                  style={{ color: "rgba(255,255,255,0.9)", borderColor: "rgba(132,204,22,0.12)" }}>
                  Blog
                </Link>
                <Link to="/login"
                  data-testid="landing-mobile-nav-login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="py-3.5 text-[15px] font-semibold inline-flex items-center gap-2 touch-target"
                  style={{ color: "rgba(255,255,255,0.9)" }}>
                  <LockIcon className="w-4 h-4" />
                  {c.nav.login}
                </Link>
              </nav>
              {/* Sticky bottom CTA — duplicated to always feel one tap away */}
              <div className="border-t px-5 py-4 pb-safe"
                   style={{ borderColor: "rgba(132,204,22,0.15)",
                              background: "rgba(15,8,32,0.98)" }}>
                <Link to="/signup"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="landing-mobile-cta"
                  className="w-full h-12 rounded-lg inline-flex items-center justify-center gap-2 text-[13.5px] font-extrabold uppercase tracking-tight"
                  style={{ backgroundColor: "#84CC16", color: "#0F172A",
                              boxShadow: "0 8px 30px -10px rgba(132,204,22,0.55)" }}>
                  {c.nav.demo}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </aside>
          </>
        )}
      </header>

      {/* === HERO === */}
      <section id="top" data-testid="hero-section" className="relative overflow-hidden">
        {/* Iter57 — Scrollytelling progress rail. */}
        <ScrollytellingRail progress={scrollProgress} lang={lang} />
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full" style={{ background: "radial-gradient(circle, rgba(132,204,22,0.12), transparent 60%)" }} />
          <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(15,23,42,0.05) 1px, transparent 0)", backgroundSize: "28px 28px" }} />
        </div>
        {/* Iter279 · 8-second proof loop above the hero — cycles Kanban
            drag → Vision analyzing → Push arriving. Pure CSS/framer
            animation, no video files, works everywhere. */}
        <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-4 sm:pt-8">
          <HeroProofLoop lang={lang} />
        </div>
        <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-4 sm:pt-6 lg:pt-10 pb-10 sm:pb-16 grid lg:grid-cols-12 gap-6 sm:gap-10 items-center">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }}
            variants={{ show: { transition: { staggerChildren: 0.07 } } }} className="lg:col-span-7">
            {/* Iter272 · Hero mobile polish — pill hidden on <sm to shave 32px */}
            <motion.div variants={fadeUp}
              className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-[11px] font-bold uppercase tracking-[0.18em]"
              style={{ borderColor: "rgba(132,204,22,0.18)", backgroundColor: "#160C2D", color: "#A1A1AA" }}>
              <Sparkles className="w-3 h-3" style={{ color: "#84CC16" }} /> {c.hero.pill}
            </motion.div>
            {/* 14-day Free Trial ribbon — kept everywhere, slimmer on mobile. */}
            <motion.div variants={fadeUp} data-testid="hero-trial-ribbon"
              className="sm:mt-4 inline-flex items-center gap-2 rounded-full font-display font-black text-[11.5px] md:text-[13px] uppercase tracking-[0.14em]"
              style={{ padding: "6px 14px", backgroundColor: "#10B981", color: "#FFFFFF", boxShadow: "0 6px 24px -8px rgba(16,185,129,0.55)" }}>
              <Zap className="w-3.5 h-3.5" />
              {lang === "pt" ? "14 dias FREE TRIAL · sem cartão"
               : lang === "es" ? "14 días FREE TRIAL · sin tarjeta"
               : "14-day FREE TRIAL · no credit card"}
            </motion.div>
            {/* Iter272 · Hero H1 collapses to 2 punchy lines on mobile.
                Desktop keeps the 4-chunk headline with lime highlight. */}
            <motion.h1 variants={fadeUp} className="font-display mt-3 sm:mt-6 text-[34px] leading-[1.02] sm:text-5xl sm:leading-[0.95] md:text-6xl lg:text-[5rem] font-black tracking-tight sm:tracking-tighter" style={{ color: "#FFFFFF" }}>
              {/* Mobile — single 2-line headline */}
              <span className="sm:hidden">
                {c.hero.h1a}{" "}
                <span className="relative inline-block">
                  <span className="relative z-10">{c.hero.h1b}</span>
                  <span className="absolute inset-x-0 bottom-1 h-2.5 rounded-sm -z-0" style={{ backgroundColor: "rgba(132,204,22,0.55)" }} />
                </span>
              </span>
              {/* Tablet+ — full 4-chunk headline */}
              <span className="hidden sm:inline">
                {c.hero.h1a}{" "}
                <span className="relative inline-block">
                  <span className="relative z-10">{c.hero.h1b}</span>
                  <span className="absolute inset-x-0 bottom-1 h-3 rounded-sm -z-0" style={{ backgroundColor: "rgba(132,204,22,0.55)" }} />
                </span>{" "}
                <span style={{ color: "#1B3A8A" }}>{c.hero.h1c}</span>{" "}
                {c.hero.h1d}
              </span>
            </motion.h1>
            {/* Subtitle — hidden on <sm to keep CTA above the fold. */}
            <motion.p variants={fadeUp} className="hidden sm:block mt-6 text-[17px] md:text-[19px] leading-relaxed max-w-2xl" style={{ color: "#A1A1AA" }}>
              {c.hero.sub}
            </motion.p>
            <motion.div variants={fadeUp} className="mt-5 sm:mt-8 flex flex-wrap items-center gap-3">
              <Link to="/signup" data-testid="hero-cta-primary"
                onClick={heroAB.trackClick}
                data-ab-variant={heroAB.variant}
                className="group inline-flex items-center gap-2 text-[#0A1233] font-bold text-[14px] tracking-wide px-6 sm:px-7 py-3.5 sm:py-4 rounded-md transition-all hover:-translate-y-0.5"
                style={{ backgroundColor: "#84CC16", boxShadow: "0 8px 30px -10px rgba(132,204,22,0.55)" }}>
                {heroAB.copy}<ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
              {/* Secondary CTA — hidden on <sm (users can scroll to product). */}
              <a href="#product" data-testid="hero-cta-secondary"
                className="hidden sm:inline-flex items-center gap-2 border-2 font-bold text-[14px] px-6 py-4 rounded-md transition-all"
                style={{ borderColor: "#0A1233", color: "#FFFFFF" }}>
                <PlayCircle className="w-4 h-4" /> {c.hero.cta2}
              </a>
            </motion.div>
            <motion.p variants={fadeUp} className="mt-4 sm:mt-5 text-[11px] sm:text-[12px] uppercase tracking-[0.18em] font-semibold" style={{ color: "#A1A1AA" }}>
              <Check className="inline w-3.5 h-3.5 mr-1.5" style={{ color: "#10B981" }} />
              {c.hero.micro}
            </motion.p>
          </motion.div>
          {/* Iter272 · Product montage hidden on mobile — it's heavy and
              pushes real content off-screen. Tablet+ keeps the full 3-frame
              floating showcase. */}
          <div className="hidden lg:block lg:col-span-5 relative h-[540px]">
            <FloatingProductMontage lang={lang} />
          </div>
        </div>
      </section>

      {/* === INTEGRATION LOGOS STRIP === */}
      <section data-testid="integrations-logos" className="border-y" style={{ borderColor: "rgba(132,204,22,0.18)", backgroundColor: "#1E293B" }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-10">
          <p className="font-mono-tech text-[10.5px] uppercase tracking-[0.28em] text-center" style={{ color: "#A1A1AA" }}>
            {c.logos.eyebrow}
          </p>
          <div className="mt-5">
            <IntegrationsLiveCount lang={lang} />
          </div>
          <div className="mt-2 grid grid-cols-3 md:grid-cols-6 gap-6 items-center">
            {INTEGRATION_LOGOS.map((logo) => (
              <div key={logo.slug} data-testid={`integration-logo-${logo.slug}`}
                className="flex flex-col items-center gap-3 opacity-80 hover:opacity-100 transition-opacity">
                <img src={logo.customSrc || BRAND(logo.slug, logo.color)} alt={logo.name}
                  className="h-14 w-14 md:h-16 md:w-16 object-contain" loading="lazy" />
                <span className="text-[11.5px] uppercase tracking-[0.18em] font-bold text-center leading-tight" style={{ color: "#A1A1AA" }}>{logo.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === SOCIAL PROOF === */}
      <section data-testid="social-proof-section" className="border-b" style={{ backgroundColor: "#160C2D", borderColor: "rgba(132,204,22,0.18)" }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-10">
          <p className="font-mono-tech text-[10.5px] uppercase tracking-[0.28em] text-center" style={{ color: "#A1A1AA" }}>{c.proof.eyebrow}</p>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-6">
            {c.proof.stats.map((s, i) => (
              <motion.div key={s.l} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
                className="text-center md:text-left">
                <div className="font-display text-4xl md:text-5xl font-black tabular-nums" style={{ color: "#FFFFFF" }}>{s.v}</div>
                <div className="mt-1 text-[12.5px] leading-snug max-w-[200px] mx-auto md:mx-0" style={{ color: "#A1A1AA" }}>{s.l}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* === BUSINESS GALLERY === */}
      <section data-testid="business-gallery" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <SectionHeader eyebrow={c.gallery.eyebrow} title={c.gallery.title} sub={c.gallery.sub} />
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {BUSINESS_GALLERY.map((b, i) => {
              const Icon = b.icon;
              const name = b[`${lang}Name`];
              return (
                <motion.a key={b.key} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
                  href={`/v/${b.key}`}
                  data-testid={`gallery-${b.key}`}
                  className="relative rounded-xl overflow-hidden aspect-[4/5] group cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#E5C572]">
                  <img src={b.img} alt={name} loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A1233] via-[#0A1233]/40 to-transparent" />
                  <div className="absolute inset-0 p-4 flex flex-col justify-end text-white">
                    <Icon className="w-5 h-5 mb-1.5" style={{ color: "#E5C572" }} />
                    <span className="font-display text-[14px] font-bold leading-tight">{name}</span>
                    <span className="mt-1 text-[10px] uppercase tracking-[0.18em] opacity-70 group-hover:opacity-100 transition-opacity">
                      {lang === "pt" ? "Ver solução →" : lang === "es" ? "Ver solución →" : "See solution →"}
                    </span>
                  </div>
                </motion.a>
              );
            })}
          </div>
        </div>
      </section>

      {/* === HOW IT WORKS === */}
      <section id="how" data-testid="how-it-works" className="py-20 lg:py-28" style={{ backgroundColor: "#1E293B", borderTop: "1px solid #E5E2DA", borderBottom: "1px solid #E5E2DA" }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <SectionHeader eyebrow={c.how.eyebrow} title={c.how.title} sub={c.how.sub} />
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10">
            {c.how.steps.map((s, i) => (
              <motion.div key={s.n} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="relative">
                <div className="font-display text-[64px] font-black leading-none" style={{ color: "rgba(132,204,22,0.18)" }}>{s.n}</div>
                <h3 className="mt-2 font-display text-xl font-bold tracking-tight" style={{ color: "#FFFFFF" }}>{s.t}</h3>
                <p className="mt-2 text-[14px] leading-relaxed" style={{ color: "#A1A1AA" }}>{s.d}</p>
                {i < c.how.steps.length - 1 && (
                  <div className="hidden lg:block absolute top-7 -right-4" style={{ color: "rgba(132,204,22,0.30)" }}>
                    <ArrowRight className="w-5 h-5" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* === FEATURE BENTO === */}
      <section id="product" data-testid="feature-grid" className="py-20 lg:py-28" style={{ backgroundColor: "#160C2D" }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <SectionHeader eyebrow={c.features.eyebrow} title={c.features.title} sub={c.features.sub} />

          {/* DRAMATIC PAIN ↔ PLEASURE — replaces the soft pastel block.
              On desktop: side-by-side with a giant VS divider + 4 hour
              counter at the top showing 4h47m wasted vs <2min with us. */}
          <ExcelVsAgenteLeadDramatic c={c} lang={lang} />

          <div className="mt-14 grid grid-cols-12 gap-4 md:gap-5 auto-rows-[minmax(180px,_auto)]">
            <BentoCard data={c.features.items.maps}     className="col-span-12 md:col-span-6 lg:col-span-4" testid="bento-maps" />
            <BentoCard data={c.features.items.ai}       className="col-span-12 md:col-span-6 lg:col-span-4" testid="bento-ai" feature />
            <BentoCard data={c.features.items.kanban}   className="col-span-12 md:col-span-6 lg:col-span-4" testid="bento-kanban" />
            <BentoCard data={c.features.items.scrapers} className="col-span-12 md:col-span-6 lg:col-span-4" testid="bento-scrapers" feature />
            <BentoCard data={c.features.items.outreach} className="col-span-12 md:col-span-6 lg:col-span-4" testid="bento-outreach" />
            <BentoCard data={c.features.items.replies}  className="col-span-12 md:col-span-12 lg:col-span-4" testid="bento-replies" />
          </div>
        </div>
      </section>

      {/* === MVP TOOLKIT (Iter278) — every module promoted in one grid === */}
      <section id="toolkit" data-testid="toolkit-section" className="py-20 lg:py-28"
        style={{ backgroundColor: "#0A1233", borderTop: "1px solid rgba(132,204,22,0.15)" }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <SectionHeader eyebrow={c.toolkit.eyebrow} title={c.toolkit.title} sub={c.toolkit.sub} />
          <div className="mt-12 space-y-10">
            {c.toolkit.groups.map((g) => (
              <motion.div key={g.label}
                initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.45 }}
                data-testid={`toolkit-group-${g.label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
                <div className="flex items-center gap-3 mb-5">
                  <span className="h-6 w-1.5 rounded-full" style={{ background: g.color }} />
                  <h3 className="font-display text-[15px] uppercase tracking-[0.22em] font-black" style={{ color: g.color }}>
                    {g.label}
                  </h3>
                  <span className="flex-1 h-px" style={{ background: `linear-gradient(90deg, ${g.color}55, transparent)` }} />
                  <span className="text-[10.5px] uppercase tracking-widest text-white/40 font-bold tabular-nums">
                    {g.items.length}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {g.items.map((it) => {
                    const Icon = it.icon;
                    const slug = it.t.toLowerCase().replace(/[^a-z0-9]/g, "-").slice(0, 30);
                    return (
                      <button key={it.t}
                        type="button"
                        onClick={() => setToolkitPreview({ ...it, groupLabel: g.label, groupColor: g.color, slug })}
                        data-testid={`toolkit-item-${slug}`}
                        className="text-left rounded-xl border p-5 h-full transition-all hover:-translate-y-0.5 hover:shadow-xl flex flex-col relative group focus:outline-none focus:ring-2 focus:ring-offset-2"
                        style={{ background: "#1E293B", borderColor: `${g.color}22` }}>
                        <div className="flex items-start justify-between mb-3">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                               style={{ background: `${g.color}18`, border: `1px solid ${g.color}44` }}>
                            <Icon className="w-5 h-5" style={{ color: g.color }} />
                          </div>
                          {it.badge && (
                            <span className="text-[9.5px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded"
                                  style={{ background: g.color, color: "#0F172A" }}>
                              {it.badge}
                            </span>
                          )}
                        </div>
                        <h4 className="font-display text-[15.5px] font-extrabold leading-tight text-white mb-1.5">
                          {it.t}
                        </h4>
                        <p className="text-[13px] leading-relaxed flex-1" style={{ color: "#A1A1AA" }}>
                          {it.d}
                        </p>
                        <span className="mt-3 text-[10.5px] uppercase tracking-widest font-black inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                              style={{ color: g.color }}>
                          {lang === "pt" ? "Ver preview" : lang === "es" ? "Ver preview" : "See preview"}
                          <ArrowRight className="w-3 h-3" />
                        </span>
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </div>
          <div className="mt-14 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/signup"
              data-testid="toolkit-cta"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md font-black text-[13.5px] uppercase tracking-tight transition-all hover:-translate-y-0.5"
              style={{ backgroundColor: "#84CC16", color: "#0F172A",
                       boxShadow: "0 10px 34px -12px rgba(132,204,22,0.65)" }}>
              {c.toolkit.cta} <ArrowRight className="w-4 h-4" />
            </Link>
            <span className="text-[11.5px] uppercase tracking-[0.18em] font-bold" style={{ color: "#A1A1AA" }}>
              14 dias FREE TRIAL · sem cartão de crédito
            </span>
          </div>
        </div>
      </section>

      {/* === SOLUTIONS === */}
      <section id="solutions" data-testid="solutions-section" className="py-20 lg:py-28" style={{ backgroundColor: "#1E293B", borderTop: "1px solid #E5E2DA", borderBottom: "1px solid #E5E2DA" }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <SectionHeader eyebrow={c.solutions.eyebrow} title={c.solutions.title} sub={c.solutions.sub} />
          <div className="mt-10 flex flex-wrap gap-2" data-testid="solutions-tabs">
            {Object.entries(c.solutions.tabs).map(([key, tab]) => (
              <button key={key} type="button" onClick={() => setSolTab(key)}
                data-testid={`solutions-tab-${key}`}
                className={`px-4 py-2.5 rounded-full text-[13px] font-bold transition-all ${solTab === key ? "text-white shadow-lg" : "hover:bg-slate-100"}`}
                style={{ backgroundColor: solTab === key ? "#0A1233" : "transparent", color: solTab === key ? "#FFF" : "#0A1233", borderWidth: solTab === key ? 0 : 1, borderStyle: "solid", borderColor: "rgba(132,204,22,0.18)" }}>
                {tab.label}
              </button>
            ))}
          </div>
          {(() => {
            const t = c.solutions.tabs[solTab];
            const Icon = solTab === "gc" ? Building : solTab === "sub" ? HardHat : PackageIcon;
            return (
              <motion.div key={solTab} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}
                data-testid={`solutions-panel-${solTab}`}
                className="mt-10 grid lg:grid-cols-12 gap-10 items-start">
                <div className="lg:col-span-7">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: "#84CC1620", color: "#8E6B2C" }}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl font-black tracking-tight leading-tight" style={{ color: "#FFFFFF" }}>{t.title}</h3>
                  <p className="mt-4 text-[16px] leading-relaxed" style={{ color: "#A1A1AA" }}>{t.body}</p>
                  <ul className="mt-6 space-y-2.5">
                    {t.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2.5 text-[14px]" style={{ color: "#FFFFFF" }}>
                        <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#10B981" }} />{b}
                      </li>
                    ))}
                  </ul>
                  <Link to="/signup" className="mt-8 inline-flex items-center gap-2 font-bold text-[13px] px-5 py-3 rounded-md transition-all"
                    style={{ backgroundColor: "#0A1233", color: "#FFF" }}
                    data-testid={`solutions-cta-${solTab}`}>{c.hero.cta1}<ArrowRight className="w-3.5 h-3.5" /></Link>
                </div>
                <div className="lg:col-span-5">
                  <div className="relative rounded-xl overflow-hidden aspect-[4/5]">
                    <img src={BUSINESS_GALLERY[solTab === "gc" ? 4 : solTab === "sub" ? 0 : 2].img}
                      alt={t.label} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A1233]/80 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4 text-white">
                      <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider" style={{ backgroundColor: "#84CC16", color: "#FFFFFF" }}>{c.solutions.eyebrow}</div>
                      <div className="mt-2 font-display text-[18px] font-bold">{t.label}</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })()}
        </div>
      </section>

      {/* === CASE STUDIES === */}
      <section id="cases" data-testid="cases-section" className="py-20 lg:py-28" style={{ backgroundColor: "#160C2D" }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <SectionHeader eyebrow={c.cases.eyebrow} title={c.cases.title} sub={c.cases.sub} />
          {/* Iter273 · Mobile — horizontal swipe deck showing all 5 KPIs
              in one flick. Desktop — tabs + single expanded panel. */}
          <CasesSwipeDeckMobile tabs={c.cases.tabs} eyebrow={c.cases.eyebrow} cta={c.hero.cta1} />
          <div className="hidden md:block">
            <div className="mt-10 flex flex-wrap gap-2" data-testid="cases-tabs">
              {c.cases.tabs.map((tab) => (
                <button key={tab.id} type="button" onClick={() => setCaseTab(tab.id)}
                  data-testid={`cases-tab-${tab.id}`}
                  className={`px-4 py-2.5 rounded-full text-[13px] font-bold transition-all ${caseTab === tab.id ? "text-white" : ""}`}
                  style={{ backgroundColor: caseTab === tab.id ? "#84CC16" : "#FFFFFF", color: caseTab === tab.id ? "#0A1233" : "#A1A1AA", borderWidth: 1, borderStyle: "solid", borderColor: caseTab === tab.id ? "#84CC16" : "rgba(132,204,22,0.18)" }}>
                  {tab.label}
                </button>
              ))}
            </div>
            {(() => {
              const t = c.cases.tabs.find((x) => x.id === caseTab) || c.cases.tabs[0];
              return (
                <motion.div key={caseTab} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}
                  data-testid={`cases-panel-${caseTab}`}
                  className="mt-10 rounded-2xl overflow-hidden border-l-4" style={{ backgroundColor: "#1E293B", borderColor: "#84CC16" }}>
                  <div className="p-8 lg:p-12 grid lg:grid-cols-12 gap-8 items-center">
                    <div className="lg:col-span-8">
                      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ backgroundColor: "#0A123310", color: "#FFFFFF" }}>
                        <BarChart3 className="w-3 h-3" /> {t.kpi}
                      </div>
                      <h3 className="mt-4 font-display text-3xl md:text-4xl font-black tracking-tight leading-tight" style={{ color: "#FFFFFF" }}>{t.title}</h3>
                      <p className="mt-4 text-[16px] leading-relaxed" style={{ color: "#A1A1AA" }}>{t.body}</p>
                      <Link to="/signup" className="mt-6 inline-flex items-center gap-2 font-bold text-[13px] px-5 py-3 rounded-md transition-all"
                        style={{ backgroundColor: "#84CC16", color: "#FFFFFF" }}
                        data-testid={`cases-cta-${caseTab}`}>{c.hero.cta1}<ArrowRight className="w-3.5 h-3.5" /></Link>
                    </div>
                    <div className="lg:col-span-4">
                      <div className="rounded-xl p-6 text-center" style={{ backgroundColor: "#0A1233", color: "#FFFFFF" }}>
                        <div className="font-mono-tech text-[10px] uppercase tracking-[0.22em] opacity-60">KPI</div>
                        <div className="font-display text-3xl md:text-4xl font-black mt-2 leading-tight" style={{ color: "#E5C572" }}>{t.kpi}</div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })()}
          </div>
        </div>
      </section>

      {/* === TESTIMONIALS === */}
      <section data-testid="testimonials" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <SectionHeader eyebrow={c.testi.eyebrow} title={c.testi.title} />
          {/* Iter272 · Mobile — swipeable snap carousel with progress dots.
              Desktop — untouched 3-column grid. */}
          <TestimonialCarouselMobile items={c.testi.items} />
          <div className="hidden md:grid md:grid-cols-3 gap-6 mt-12">
            {c.testi.items.map((t, i) => (
              <motion.figure key={t.a} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
                data-testid={`testimonial-${i}`}
                className="border p-7 rounded-xl hover:shadow-xl hover:-translate-y-1 transition-all flex flex-col"
                style={{ backgroundColor: "#1E293B", borderColor: "rgba(132,204,22,0.18)" }}>
                <div className="flex gap-0.5 mb-4" style={{ color: "#84CC16" }}>
                  {[1, 2, 3, 4, 5].map((n) => <Star key={n} className="w-4 h-4" fill="#84CC16" />)}
                </div>
                <blockquote className="font-display text-[18px] font-semibold leading-snug flex-1" style={{ color: "#FFFFFF" }}>&ldquo;{t.q}&rdquo;</blockquote>
                <figcaption className="mt-6 flex items-center gap-3 pt-5 border-t" style={{ borderColor: "rgba(132,204,22,0.18)" }}>
                  <img src={AVATARS[i % AVATARS.length]} alt={t.a} className="w-10 h-10 rounded-full object-cover ring-2" style={{ "--tw-ring-color": "rgba(132,204,22,0.3)" }} />
                  <div className="font-display text-[13px] font-bold" style={{ color: "#FFFFFF" }}>{t.a}</div>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* === COMPARE === */}
      <section id="compare" data-testid="compare-section" className="py-20 lg:py-28" style={{ backgroundColor: "#160C2D", borderTop: "1px solid #E5E2DA" }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <SectionHeader eyebrow={c.compare.eyebrow} title={c.compare.title} sub={c.compare.sub} />

          {/* Mobile compact view — feature-by-feature stack with AgenteLead
              as the hero and competitor chips below. Replaces the legacy
              table that simply does NOT fit a 390px viewport. */}
          <CompareMobileStack compare={c.compare} lang={lang} cta={c.hero.cta1} />

          {/* Desktop wide table — md+ */}
          <motion.div className="hidden md:block mt-14 max-w-5xl mx-auto rounded-2xl border-2 shadow-xl overflow-hidden"
            style={{ backgroundColor: "#1E293B", borderColor: "#84CC16" }}
            data-testid="compare-table"
            initial={{ opacity: 0, y: 32, scale: 0.985 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
            <table className="w-full text-left table-fixed">
              <colgroup>
                <col style={{ width: "28%" }} />
                <col style={{ width: "22%" }} />
                <col style={{ width: "16.6%" }} />
                <col style={{ width: "16.6%" }} />
                <col style={{ width: "16.6%" }} />
              </colgroup>
              <thead style={{ backgroundColor: "#160C2D" }}>
                <tr>
                  <th className="font-mono-tech text-[10.5px] uppercase tracking-[0.22em] py-5 px-5" style={{ color: "#A1A1AA" }}>
                    {lang === "pt" ? "Recurso" : lang === "es" ? "Recurso" : "Feature"}
                  </th>
                  {c.compare.cols.map((col) => (
                    <th key={col.k}
                      data-testid={`compare-col-${col.k}`}
                      className="font-display text-[13px] md:text-[15px] font-bold py-5 px-3 text-center relative"
                      style={{
                        color: col.highlight ? "#84CC16" : "#FFFFFF",
                        backgroundColor: col.highlight ? "rgba(132,204,22,0.12)" : "transparent",
                        borderBottom: col.highlight ? "3px solid #84CC16" : "none",
                      }}>
                      {col.highlight && (
                        <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap text-[9px] font-black uppercase tracking-[0.18em] px-2.5 py-1 rounded-full shadow-md"
                          style={{ backgroundColor: "#84CC16", color: "#0F172A" }}>
                          ★ {lang === "pt" ? "Nossa escolha" : lang === "es" ? "Nuestra elección" : "Our pick"}
                        </span>
                      )}
                      {col.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {c.compare.features.map((f, i) => (
                  <motion.tr key={f.k} data-testid={`compare-row-${f.k}`} style={{ borderTop: "1px solid rgba(132,204,22,0.12)", backgroundColor: i % 2 === 0 ? "#1E293B" : "#180C32" }}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.55, ease: "easeOut", delay: 0.08 + i * 0.07 }}>
                    <td className="py-4 px-5 text-[13px] font-semibold" style={{ color: "#FFFFFF" }}>{f.l}</td>
                    {c.compare.cols.map((col) => {
                      const cell = col.cells[f.k];
                      const isHL = col.highlight;
                      const isBool = typeof cell === "boolean";
                      return (
                        <td key={col.k} className="py-4 px-3 text-center" style={{ backgroundColor: isHL ? "rgba(132,204,22,0.08)" : "transparent" }}>
                          {isBool ? (
                            cell
                              ? (isHL
                                  ? <span className="inline-flex items-center justify-center w-6 h-6 rounded-full" style={{ backgroundColor: "#10B981" }}>
                                      <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                                    </span>
                                  : <Check className="w-4 h-4 inline" style={{ color: "#10B981" }} />)
                              : <span className="text-[13px] font-semibold" style={{ color: "rgba(255,255,255,0.30)" }}>—</span>
                          ) : (
                            <span className={`text-[13px] font-black tabular-nums ${f.k === "price" ? "font-display text-[15px]" : ""}`}
                              style={{ color: isHL ? "#84CC16" : "#FFFFFF" }}>
                              {cell}
                              {isHL && f.k === "price" && (
                                <div className="text-[9px] uppercase tracking-wider mt-0.5" style={{ color: "#84CC16" }}>{lang === "pt" ? "Melhor custo" : lang === "es" ? "Mejor costo" : "Best value"}</div>
                              )}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </motion.div>
          <motion.div className="mt-10 text-center"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}>
            <Link to="/signup" className="inline-flex items-center gap-2 font-bold text-[14px] px-7 py-4 rounded-md transition-all hover:-translate-y-0.5"
              style={{ backgroundColor: "#84CC16", color: "#FFFFFF", boxShadow: "0 8px 30px -10px rgba(132,204,22,0.55)" }}
              data-testid="compare-cta">
              {c.hero.cta1}<ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* === PUBLIC DEMO (Lead Card mock) === */}
      <section id="demo" data-testid="public-demo" className="py-20 lg:py-28" style={{ backgroundColor: "#0F172A" }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <SectionHeader
            eyebrow={lang === "pt" ? "Demo ao vivo" : lang === "es" ? "Demo en vivo" : "Live demo"}
            title={lang === "pt" ? "Veja o AgenteLead Pro em ação." : lang === "es" ? "Mira AgenteLead Pro en acción." : "See AgenteLead Pro in action."}
            sub={lang === "pt"
              ? "Um exemplo real do nosso cockpit: lead Roofing/Miami, score 92/100, gap de SEO detectado e sugestão de IA pronta pra usar."
              : lang === "es"
                ? "Un ejemplo real de nuestro cockpit: lead Roofing/Miami, score 92/100, gap de SEO detectado y sugerencia de IA lista para usar."
                : "A real example of our cockpit: Roofing/Miami lead, 92/100 score, SEO gap detected and AI suggestion ready to use."} />
          <div className="mt-12 grid lg:grid-cols-2 gap-8 items-stretch">
            {/* Lead Card mock */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
              data-testid="demo-lead-card" className="rounded-2xl border p-7 relative overflow-hidden"
              style={{ backgroundColor: "#1E293B", borderColor: "rgba(132,204,22,0.25)", boxShadow: "0 12px 48px -16px rgba(132,204,22,0.18)" }}>
              <div className="flex items-center justify-between">
                <div className="font-mono-tech text-[10.5px] uppercase tracking-[0.22em]" style={{ color: "#84CC16" }}>
                  <MapPin className="inline w-3 h-3 mr-1" /> Roofing · Miami, FL
                </div>
                <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider" style={{ backgroundColor: "#EF4444", color: "#FFFFFF" }}>HOT</span>
              </div>
              <div className="mt-4">
                <h3 className="font-display text-2xl font-black" style={{ color: "#FFFFFF" }}>Coastal Roofing Co.</h3>
                <div className="text-[12.5px] mt-1" style={{ color: "#A1A1AA" }}>2300 Henderson Dr · ★ 4.8 · 87 reviews · coastalroofing.com</div>
              </div>
              <div className="mt-5">
                <div className="flex items-center justify-between text-[11px] mb-1.5" style={{ color: "#A1A1AA" }}>
                  <span className="font-mono-tech tracking-wider">LEAD SCORE</span>
                  <span className="font-bold tabular-nums" style={{ color: "#84CC16" }}>92/100</span>
                </div>
                <div className="h-2 rounded-full" style={{ backgroundColor: "rgba(255,255,255,0.06)" }}>
                  <div className="h-full rounded-full" style={{ width: "92%", background: "linear-gradient(to right, #F59E0B, #84CC16)" }} />
                </div>
              </div>
              <div className="mt-6 grid grid-cols-3 gap-2.5">
                {[
                  { l: "Site", v: "Active", ok: true },
                  { l: "SEO", v: "Gap", ok: false },
                  { l: "Reviews", v: "87★", ok: true },
                ].map((s) => (
                  <div key={s.l} className="rounded-lg p-2.5" style={{ backgroundColor: "#0F172A", border: "1px solid rgba(132,204,22,0.12)" }}>
                    <div className="text-[10px] font-mono-tech uppercase" style={{ color: "#71717A" }}>{s.l}</div>
                    <div className="text-[12px] font-bold mt-0.5" style={{ color: s.ok ? "#10B981" : "#F59E0B" }}>{s.v}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* AI Audit mock */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.15 }}
              data-testid="demo-ai-audit" className="rounded-2xl border p-7 relative overflow-hidden"
              style={{ backgroundColor: "#1E293B", borderColor: "rgba(132,204,22,0.25)" }}>
              <div className="absolute -top-20 -right-20 w-[300px] h-[300px] rounded-full blur-3xl pointer-events-none" style={{ background: "radial-gradient(circle, rgba(132,204,22,0.18), transparent 60%)" }} />
              <div className="font-mono-tech text-[10.5px] uppercase tracking-[0.22em] mb-4" style={{ color: "#E5C572" }}>
                <Sparkles className="inline w-3 h-3 mr-1" /> AI Audit · GPT
              </div>
              <h3 className="font-display text-xl font-bold leading-snug" style={{ color: "#FFFFFF" }}>
                {lang === "pt" ? "Oportunidade detectada: SEO local sem schema markup." : lang === "es" ? "Oportunidad detectada: SEO local sin schema markup." : "Opportunity detected: local SEO missing schema markup."}
              </h3>
              <ul className="mt-4 space-y-2.5 text-[13.5px]" style={{ color: "#A1A1AA" }}>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#10B981" }} />{lang === "pt" ? "Falta schema LocalBusiness em coastalroofing.com" : lang === "es" ? "Falta schema LocalBusiness en coastalroofing.com" : "Missing LocalBusiness schema on coastalroofing.com"}</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#10B981" }} />{lang === "pt" ? "Velocidade Mobile: 41/100 (PageSpeed)" : lang === "es" ? "Velocidad Mobile: 41/100 (PageSpeed)" : "Mobile speed: 41/100 (PageSpeed)"}</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#10B981" }} />{lang === "pt" ? "Sem GMB Posts nos últimos 60 dias" : lang === "es" ? "Sin GMB Posts en los últimos 60 días" : "No GMB Posts in the last 60 days"}</li>
              </ul>
              <div className="mt-5 rounded-lg p-3.5" style={{ backgroundColor: "#0F172A", border: "1px solid rgba(132,204,22,0.18)" }}>
                <div className="text-[10px] font-mono-tech uppercase tracking-wider mb-1.5" style={{ color: "#84CC16" }}>
                  {lang === "pt" ? "Sugestão de pitch IA" : lang === "es" ? "Sugerencia de pitch IA" : "AI pitch suggestion"}
                </div>
                <p className="text-[13px] leading-relaxed italic" style={{ color: "#FFFFFF" }}>
                  {lang === "pt"
                    ? "\"Olá Coastal — notamos que sua presença local pode capturar mais 35-40% de leads orgânicos com 3 ajustes simples. Posso enviar a auditoria completa em PDF?\""
                    : lang === "es"
                      ? "\"Hola Coastal — notamos que tu presencia local puede capturar 35-40% más leads orgánicos con 3 ajustes simples. ¿Te envío la auditoría completa en PDF?\""
                      : "\"Hi Coastal — we noticed your local presence could capture 35-40% more organic leads with 3 simple fixes. Can I send the full audit as PDF?\""}
                </p>
              </div>
              <Link to="/signup" data-testid="demo-cta"
                className="mt-5 inline-flex items-center gap-2 font-bold text-[13px] px-5 py-3 rounded-md transition-all hover:-translate-y-0.5"
                style={{ backgroundColor: "#84CC16", color: "#0F172A" }}>
                {lang === "pt" ? "Tente com seu lead" : lang === "es" ? "Pruébalo con tu lead" : "Try it with your lead"}
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* === ROI CALCULATOR === */}
      <RoiCalculator lang={lang} />

      {/* === B2C LEAD RADAR (NEW) === */}
      <B2CRadarSection lang={lang} />

      {/* === CLAIRE'S WINNING STRATEGY (NEW) === */}
      <WinningStrategySection lang={lang} />

      {/* === PRICING === */}
      <StrategicComparison lang={lang} />

      <PricingSection lang={lang} />

      {/* === CTA BANNER === */}
      <section id="cta" data-testid="cta-banner" className="relative overflow-hidden" style={{ backgroundColor: "#0A1233", color: "#FFFFFF" }}>
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full blur-3xl pointer-events-none" style={{ background: "radial-gradient(circle, rgba(132,204,22,0.18), transparent 60%)" }} />
        <div className="max-w-5xl mx-auto px-5 lg:px-10 py-24 lg:py-32 relative">
          <div className="text-center">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[11px] font-bold uppercase tracking-[0.18em]" style={{ borderColor: "rgba(132,204,22,0.5)", backgroundColor: "rgba(132,204,22,0.12)", color: "#E5C572" }}>
              <Zap className="w-3 h-3" /> {c.cta.pill}
            </span>
            <h2 className="font-display mt-6 text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05]">{c.cta.title}</h2>
            <p className="mt-5 text-[16px] md:text-[18px] leading-relaxed max-w-2xl mx-auto" style={{ color: "rgba(255,255,255,0.72)" }}>{c.cta.sub}</p>
          </div>
          <form onSubmit={handleSubmit} data-testid="cta-form" className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl mx-auto">
            {[{k:"name",t:c.cta.form.name,req:true},{k:"company",t:c.cta.form.company},{k:"email",t:c.cta.form.email,req:true,type:"email"},{k:"phone",t:c.cta.form.phone}].map((f) => (
              <input key={f.k} value={form[f.k]} required={f.req} type={f.type || "text"}
                onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                placeholder={f.t} data-testid={`cta-form-${f.k}`}
                className="h-12 px-4 rounded-md outline-none text-[14px] transition-colors"
                style={{ backgroundColor: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)", color: "#FFF" }} />
            ))}
            <button type="submit" disabled={submitting} data-testid="cta-form-submit"
              className="md:col-span-2 h-13 mt-2 inline-flex items-center justify-center gap-2 font-bold text-[14px] tracking-wide px-7 py-4 rounded-md transition-all hover:-translate-y-0.5 disabled:opacity-60"
              style={{ backgroundColor: "#84CC16", color: "#FFFFFF" }}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {submitting ? c.cta.form.sending : c.cta.form.submit}
            </button>
            {submitted && (
              <div data-testid="cta-form-success" className="md:col-span-2 text-center text-[13px] font-semibold inline-flex items-center justify-center gap-2 mt-1" style={{ color: "#6EE7B7" }}>
                <Check className="w-4 h-4" /> {c.cta.form.success}
              </div>
            )}
            {formError && (<div data-testid="cta-form-error" className="md:col-span-2 text-center text-[13px] mt-1" style={{ color: "#FECACA" }}>{formError}</div>)}
            <p className="md:col-span-2 text-center text-[11.5px] mt-2" style={{ color: "rgba(255,255,255,0.5)" }}>{c.cta.micro}</p>
          </form>
        </div>
      </section>

      {/* === FAQ === */}
      <section id="faq" data-testid="faq-section" className="py-20 lg:py-28">
        <div className="max-w-3xl mx-auto px-5 lg:px-10">
          <SectionHeader eyebrow={c.faq.eyebrow} title={c.faq.title} />
          <div className="mt-12 border-t" style={{ borderColor: "rgba(132,204,22,0.18)" }}>
            {c.faq.items.map((it, i) => (
              <FaqRow key={it.q} idx={i} q={it.q} a={it.a} open={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? -1 : i)} />
            ))}
          </div>
        </div>
      </section>

      {/* === FOOTER === */}
      <footer data-testid="footer" style={{ backgroundColor: "#0A1233", color: "#FFFFFF" }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-16 pb-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 pb-12 border-b" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
            <div className="lg:col-span-6">
              <div className="font-display text-[44px] md:text-[64px] lg:text-[88px] font-black tracking-tighter leading-[0.95]">
                AgenteLead<span style={{ color: "#84CC16" }}>.</span>
              </div>
              <p className="mt-3 text-[13px] uppercase tracking-[0.22em] font-bold" style={{ color: "rgba(229,197,114,0.8)" }}>{c.footer.tag}</p>
              <p className="mt-6 text-[15px] max-w-md" style={{ color: "rgba(255,255,255,0.6)" }}>{c.footer.cta}</p>
              <Link to="/signup" className="mt-4 inline-flex items-center gap-2 font-bold text-[13px] px-6 py-3 rounded-md transition-all" style={{ backgroundColor: "#84CC16", color: "#FFFFFF" }} data-testid="footer-cta">
                {c.footer.tryButton}<ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <FooterCol title={c.footer.product} items={c.footer.links.product} />
            <FooterCol title={c.footer.company} items={c.footer.links.company} />
            <FooterCol title={c.footer.legal} items={c.footer.links.legal} />
          </div>
          <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-[12px]" style={{ color: "rgba(255,255,255,0.45)" }}>{c.footer.copy}</div>
            <LangSwitcher lang={lang} setLang={setLang} dark />
          </div>
          {/* Iter100 — geo-landing internal links: surface a handful of top
              cities so Google can reach the long-tail pages from the home
              page in ONE click. Full directory lives at /cities. */}
          <div className="mt-10 pt-8 border-t" style={{ borderColor: "rgba(255,255,255,0.08)" }}
               data-testid="footer-geo-strip">
            <div className="flex items-baseline justify-between mb-3">
              <p className="text-[11px] uppercase tracking-[0.22em] font-bold"
                 style={{ color: "rgba(229,197,114,0.8)" }}>
                Find leads by city
              </p>
              <Link to="/cities" className="text-[11.5px] underline"
                    data-testid="footer-cities-all"
                    style={{ color: "rgba(255,255,255,0.55)" }}>
                See all 250+ cities →
              </Link>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {[
                "leads-orlando-fl", "leads-miami-fl", "leads-tampa-fl",
                "leads-new-york-ny", "leads-los-angeles-ca", "leads-houston-tx",
                "leads-dallas-tx", "leads-chicago-il", "leads-atlanta-ga",
                "leads-phoenix-az", "leads-seattle-wa", "leads-boston-ma",
                "leads-denver-co", "leads-philadelphia-pa", "leads-las-vegas-nv",
              ].map((s) => (
                <Link key={s} to={`/${s}`}
                  data-testid={`footer-geo-${s}`}
                  className="px-2.5 py-1 rounded-md border text-[11.5px] transition-colors"
                  style={{
                    borderColor: "rgba(255,255,255,0.10)",
                    color: "rgba(255,255,255,0.65)",
                    backgroundColor: "rgba(255,255,255,0.03)",
                  }}>
                  {s.replace("leads-", "").replace(/-([a-z]{2})$/, ", $1").replace(/-/g, " ").replace(/, ([a-z]{2})/, (_, x) => `, ${x.toUpperCase()}`)}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </footer>
      {/* Iter272 · Sticky mobile trial bar — pinned above the fold so the
          primary CTA is always thumb-reachable on smartphones. Desktop
          untouched. */}
      <StickyMobileTrialBar lang={lang} />
      {/* Iter279 · Toolkit preview lightbox — with iter280 prev/next nav */}
      <ToolkitLightbox
        item={toolkitPreview}
        lang={lang}
        allItems={allToolkitItems}
        onNavigate={(next) => setToolkitPreview(next)}
        onClose={() => setToolkitPreview(null)}
      />
    </div>
  );
}

// ----------------------------------------------------------------------------
// Sub-components
// ----------------------------------------------------------------------------

/**
 * ToolkitLightbox — Iter279
 *
 * Renders a full-screen preview of the toolkit tile clicked. Because
 * we don't ship pre-captured screenshots (marketing-safe + smaller
 * bundle), each preview is a CSS-mocked panel that resembles the
 * actual module UI. This keeps things fast, on-brand, and dark/light
 * mode friendly. Includes an ESC + backdrop close and a signup CTA.
 */
function ToolkitLightbox({ item, lang, allItems = [], onNavigate, onClose }) {
  // Iter280 · Prev/next navigation — computes the current index in the
  // flattened items list and exposes go(±1) that wraps around so
  // visitors can flip through all 20 modules without closing.
  const currentIdx = item ? allItems.findIndex((x) => x.slug === item.slug) : -1;
  const total = allItems.length;
  const go = (delta) => {
    if (!item || total === 0) return;
    const next = allItems[(currentIdx + delta + total) % total];
    if (next && onNavigate) onNavigate(next);
  };
  useEffect(() => {
    if (!item) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
    document.addEventListener("keydown", onKey);
    // lock body scroll
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, currentIdx, total]);
  if (!item) return null;

  const Icon = item.icon;
  const t = {
    cta: { pt: "Testar grátis", en: "Try it free", es: "Probar gratis" }[lang] || "Try it free",
    trial: {
      pt: "14 dias · sem cartão de crédito",
      en: "14 days · no credit card",
      es: "14 días · sin tarjeta",
    }[lang] || "14 days · no credit card",
    live: { pt: "Preview ao vivo", en: "Live preview", es: "Preview en vivo" }[lang] || "Live preview",
    close: { pt: "Fechar", en: "Close", es: "Cerrar" }[lang] || "Close",
    prev: { pt: "Anterior", en: "Previous", es: "Anterior" }[lang] || "Previous",
    next: { pt: "Próximo", en: "Next", es: "Siguiente" }[lang] || "Next",
    counter: { pt: "de", en: "of", es: "de" }[lang] || "of",
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center px-4"
         data-testid="toolkit-lightbox"
         role="dialog"
         aria-modal="true"
         aria-label={item.t}>
      {/* Backdrop */}
      <button type="button"
        onClick={onClose}
        data-testid="toolkit-lightbox-backdrop"
        aria-label={t.close}
        className="absolute inset-0"
        style={{ background: "rgba(2,6,23,0.85)", backdropFilter: "blur(8px)" }} />
      {/* Iter280 · Floating prev / next arrows — desktop-first placement
          just outside the panel; mobile falls back inside the header row. */}
      {total > 1 && (
        <>
          <button type="button"
            onClick={(e) => { e.stopPropagation(); go(-1); }}
            data-testid="toolkit-lightbox-prev"
            aria-label={t.prev}
            title={`${t.prev} (←)`}
            className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full items-center justify-center text-white transition-all hover:scale-110 z-10"
            style={{ background: "rgba(255,255,255,0.08)", border: `1px solid ${item.groupColor}55` }}>
            <ArrowLeft className="w-5 h-5" />
          </button>
          <button type="button"
            onClick={(e) => { e.stopPropagation(); go(1); }}
            data-testid="toolkit-lightbox-next"
            aria-label={t.next}
            title={`${t.next} (→)`}
            className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full items-center justify-center text-white transition-all hover:scale-110 z-10"
            style={{ background: "rgba(255,255,255,0.08)", border: `1px solid ${item.groupColor}55` }}>
            <ArrowRight className="w-5 h-5" />
          </button>
        </>
      )}
      {/* Panel */}
      <motion.div
        initial={{ opacity: 0, scale: 0.92, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-2xl border shadow-2xl"
        style={{ background: "#1E293B", borderColor: `${item.groupColor}55` }}
        data-testid="toolkit-lightbox-panel">
        {/* Header */}
        <div className="flex items-center gap-3 p-5 border-b" style={{ borderColor: `${item.groupColor}22` }}>
          <div className="w-12 h-12 rounded-lg flex items-center justify-center"
               style={{ background: `${item.groupColor}20`, border: `1px solid ${item.groupColor}55` }}>
            <Icon className="w-6 h-6" style={{ color: item.groupColor }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] font-black" style={{ color: item.groupColor }}>
              {item.groupLabel}
            </div>
            <h3 className="font-display text-[20px] sm:text-[24px] font-black leading-tight text-white">
              {item.t}
            </h3>
          </div>
          {item.badge && (
            <span className="text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded"
                  style={{ background: item.groupColor, color: "#0F172A" }}>
              {item.badge}
            </span>
          )}
          {/* Iter287 · Deep-link copy — grabs current URL (already has ?tool=slug) */}
          <button type="button"
            data-testid="toolkit-lightbox-copy-link"
            aria-label="Copy shareable link"
            title="Copy shareable link to this tool preview"
            onClick={() => {
              const url = new URL(window.location.href);
              if (item?.slug) url.searchParams.set("tool", item.slug);
              url.hash = "toolkit";
              navigator.clipboard?.writeText(url.toString()).catch(() => {});
            }}
            className="w-9 h-9 rounded-full hover:bg-white/10 text-white/70 hover:text-white flex items-center justify-center transition-colors">
            <LinkIcon className="w-4 h-4" />
          </button>
          <button type="button"
            onClick={onClose}
            data-testid="toolkit-lightbox-close"
            aria-label={t.close}
            className="w-9 h-9 rounded-full hover:bg-white/10 text-white/70 hover:text-white flex items-center justify-center transition-colors">
            <XIcon className="w-5 h-5" />
          </button>
        </div>
        {/* Body */}
        <div className="p-5 sm:p-6 space-y-5">
          <p className="text-[15px] leading-relaxed" style={{ color: "#CBD5E1" }}>
            {item.d}
          </p>
          {/* CSS-mocked preview panel */}
          <div data-testid="toolkit-lightbox-preview"
               className="rounded-xl border p-4 sm:p-5"
               style={{ background: "linear-gradient(135deg,#0F172A 0%,#160C2D 100%)",
                        borderColor: `${item.groupColor}33` }}>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-2 h-2 rounded-full bg-red-500/70" />
              <span className="w-2 h-2 rounded-full bg-amber-500/70" />
              <span className="w-2 h-2 rounded-full bg-emerald-500/70" />
              <span className="ml-2 text-[10px] uppercase tracking-widest font-black" style={{ color: item.groupColor }}>
                {t.live} · {item.t}
              </span>
            </div>
            <ToolkitPreviewMock slug={item.slug} color={item.groupColor} />
          </div>
        </div>
        {/* Footer */}
        <div className="p-5 border-t flex flex-col sm:flex-row items-center gap-3"
             style={{ borderColor: `${item.groupColor}22`, background: "rgba(0,0,0,0.25)" }}>
          <Link to="/signup"
            data-testid="toolkit-lightbox-cta"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 h-11 px-6 rounded-md font-black uppercase tracking-tight text-[13px]"
            style={{ background: item.groupColor, color: "#0F172A",
                     boxShadow: `0 8px 30px -10px ${item.groupColor}88` }}>
            {t.cta} <ArrowRight className="w-4 h-4" />
          </Link>
          <span className="text-[11px] uppercase tracking-[0.18em] font-bold flex-1 text-center sm:text-left" style={{ color: "#94A3B8" }}>
            {t.trial}
          </span>
          {/* Iter280 · counter + mobile arrows */}
          {total > 1 && (
            <div className="flex items-center gap-2" data-testid="toolkit-lightbox-nav">
              <button type="button"
                onClick={() => go(-1)}
                data-testid="toolkit-lightbox-prev-mobile"
                aria-label={t.prev}
                className="md:hidden w-9 h-9 rounded-full flex items-center justify-center text-white/85"
                style={{ background: "rgba(255,255,255,0.08)", border: `1px solid ${item.groupColor}55` }}>
                <ArrowLeft className="w-4 h-4" />
              </button>
              <span data-testid="toolkit-lightbox-counter"
                    className="text-[11px] tabular-nums uppercase tracking-widest font-black"
                    style={{ color: item.groupColor }}>
                {currentIdx + 1} <span className="text-white/50">{t.counter}</span> {total}
              </span>
              <button type="button"
                onClick={() => go(1)}
                data-testid="toolkit-lightbox-next-mobile"
                aria-label={t.next}
                className="md:hidden w-9 h-9 rounded-full flex items-center justify-center text-white/85"
                style={{ background: "rgba(255,255,255,0.08)", border: `1px solid ${item.groupColor}55` }}>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

/**
 * ToolkitPreviewMock — small CSS "screenshot" per module slug. Falls
 * back to a generic grid mock so unmapped items still render nicely.
 */
function ToolkitPreviewMock({ slug, color }) {
  // High-signal mocks — the modules users pay to see. Everything else
  // gets the generic 3-row skeleton fallback.
  if (slug === "kanban-crm") {
    return (
      <div className="grid grid-cols-3 gap-2">
        {["Novo", "Qualificado", "Fechado"].map((col) => (
          <div key={col} className="rounded border border-white/10 p-2 h-[120px]">
            <div className="text-[8.5px] uppercase tracking-widest text-white/45 mb-1">{col}</div>
            <div className="space-y-1">
              <div className="h-3 rounded bg-white/8" />
              <div className="h-3 rounded bg-white/8" />
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (slug === "command-hq") {
    return (
      <div className="grid grid-cols-4 gap-2">
        {["ACV", "Hot", "Missing", "Repo"].map((k, i) => (
          <div key={k} className="rounded border border-white/10 p-2">
            <div className="text-[8.5px] uppercase tracking-widest" style={{ color }}>{k}</div>
            <div className="text-[16px] font-black tabular-nums text-white mt-0.5">{["$167M","1","45/56","41"][i]}</div>
          </div>
        ))}
      </div>
    );
  }
  if (slug === "vision-analyzer") {
    return (
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded relative overflow-hidden h-[130px]" style={{ background: "linear-gradient(135deg,#1F2937,#0B1220)" }}>
          <motion.div className="absolute inset-x-0 h-0.5"
            style={{ background: `linear-gradient(90deg, transparent, ${color}, transparent)`, boxShadow: `0 0 10px ${color}` }}
            initial={{ top: 0 }} animate={{ top: ["0%", "100%", "0%"] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }} />
          <div className="absolute top-2 left-2 text-[9px] uppercase font-black tracking-widest px-1.5 py-0.5 rounded"
               style={{ background: color, color: "#0F172A" }}>
            Sonnet 5
          </div>
        </div>
        <div className="rounded border border-white/10 p-2.5 text-[11px] text-white/85 leading-snug">
          <div className="text-[8.5px] uppercase tracking-widest mb-1" style={{ color }}>Pitch gerado</div>
          "Garagem sem sensor + janelas sem película — pacote Guardian Pro cobre exatamente isso."
        </div>
      </div>
    );
  }
  if (slug === "claire-dual-engine") {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded" style={{ background: "#7C3AED", color: "#fff" }}>Deep</span>
          <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded" style={{ background: "#10B981", color: "#0F172A" }}>Fast</span>
          <span className="text-[9.5px] text-white/50">motor</span>
        </div>
        <div className="rounded border border-white/10 bg-black/30 p-2 text-[11px] text-white/85">Como importo 500 leads?</div>
        <div className="rounded border border-white/10 p-2 text-[11px] text-white/85" style={{ background: `${color}0F`, borderColor: `${color}33` }}>
          Vá em <b>Imports</b>, arraste seu CSV e mapeie as colunas. Em ~30s você já tem 500 prospects scoreados no Kanban.
        </div>
      </div>
    );
  }
  if (slug === "push-notifications") {
    return (
      <div className="flex items-center justify-center h-[140px]">
        <div className="w-[200px] h-[130px] rounded-[16px] border-2 relative overflow-hidden"
             style={{ background: "#000", borderColor: "rgba(255,255,255,0.15)" }}>
          <div className="absolute inset-x-0 top-2 text-center text-white/70 text-[10px] font-bold tabular-nums">09:41</div>
          <motion.div className="absolute left-2 right-2 top-8 rounded-lg p-2 flex items-start gap-1.5"
            style={{ background: "rgba(30,41,59,0.98)", border: `1px solid ${color}55` }}
            initial={{ y: -50, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}>
            <div className="w-5 h-5 rounded flex items-center justify-center shrink-0" style={{ background: color }}>
              <Zap className="w-3 h-3" style={{ color: "#0F172A" }} />
            </div>
            <div className="text-[10px] font-bold text-white leading-tight">1 HOT lead · Score 92</div>
          </motion.div>
        </div>
      </div>
    );
  }
  // Generic fallback — colored bars mimicking a compact list view.
  return (
    <div className="space-y-2">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="h-2.5 w-2.5 rounded-full" style={{ background: color, opacity: 1 - i * 0.15 }} />
          <div className="h-2.5 rounded flex-1" style={{ background: `${color}${i === 0 ? "88" : "33"}`, width: `${100 - i * 10}%` }} />
          <div className="text-[10px] tabular-nums text-white/50 w-10 text-right">{[92, 74, 68, 55, 43][i]}</div>
        </div>
      ))}
    </div>
  );
}

/**
 * HeroProofLoop — Iter279 · 8-second proof loop above the hero
 *
 * Three CSS-animated scenes cycle every ~2.7 seconds:
 *   1. Kanban drag       — a card lifts from Novo → Qualificado
 *   2. Vision analyzing  — a property photo gets scanned and returns a
 *      security pitch, live
 *   3. Push notification — a native-style toast slides in with "1 HOT lead"
 *
 * Pure framer-motion + CSS, zero video files. Small compact frame so it
 * doesn't push the H1 out of the fold on mobile.
 */
function HeroProofLoop({ lang }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % 3), 2700);
    return () => clearInterval(t);
  }, []);
  const labels = {
    pt: ["Kanban", "Command HQ", "Mobile PWA"],
    en: ["Kanban", "Command HQ", "Mobile PWA"],
    es: ["Kanban", "Command HQ", "Mobile PWA"],
  }[lang] || ["Kanban", "Command HQ", "Mobile PWA"];
  const captions = {
    pt: ["Drag-and-drop entre colunas", "Cockpit executivo ao vivo", "Instala e alerta no bolso"],
    en: ["Drag-and-drop across columns",  "Executive cockpit, live",     "Installs + alerts in pocket"],
    es: ["Arrastra entre columnas",       "Cockpit ejecutivo en vivo",   "Se instala y alerta en el bolsillo"],
  }[lang] || ["Drag across columns", "Executive cockpit live", "Installs + alerts in pocket"];

  // Iter280 · real screenshots captured from the actual app instead of
  // CSS mocks. Static frames crossfade every 2.7s — matches the "video
  // loop" spirit without shipping an MP4.
  const scenes = [
    { img: "/proof/scene-kanban.jpg",  Overlay: KanbanOverlay },
    { img: "/proof/scene-hq.jpg",      Overlay: HqOverlay },
    { img: "/proof/scene-mobile.jpg",  Overlay: MobileOverlay },
  ];

  return (
    <div data-testid="hero-proof-loop"
         className="relative rounded-xl border overflow-hidden"
         style={{ background: "linear-gradient(135deg,#0F172A 0%,#160C2D 100%)",
                  borderColor: "rgba(132,204,22,0.22)",
                  boxShadow: "0 12px 40px -14px rgba(132,204,22,0.35)" }}>
      {/* Top rail — 3 progress bars, active one fills over the interval */}
      <div className="flex gap-1.5 p-2.5 border-b border-white/5"
           data-testid="hero-proof-rail">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex-1 h-1 rounded-full overflow-hidden bg-white/8">
            <motion.div
              className="h-full"
              style={{ background: "linear-gradient(90deg,#84CC16,#10B981)" }}
              initial={{ width: 0 }}
              animate={{ width: i === idx ? "100%" : (i < idx ? "100%" : 0) }}
              transition={{ duration: i === idx ? 2.6 : 0.25, ease: "linear" }}
            />
          </div>
        ))}
      </div>

      {/* Stage — real app screenshots crossfading with framer */}
      <div className="relative h-[220px] sm:h-[280px] overflow-hidden bg-black">
        {scenes.map((sc, i) => (
          <motion.div
            key={i}
            className="absolute inset-0"
            initial={false}
            animate={{ opacity: idx === i ? 1 : 0 }}
            transition={{ duration: 0.55 }}
            style={{ pointerEvents: idx === i ? "auto" : "none" }}
            data-testid={`hero-proof-scene-${i}`}>
            <img src={sc.img}
                 alt={labels[i]}
                 loading="eager"
                 className="w-full h-full object-cover object-top" />
            {/* soft dark bottom fade for legibility of the caption strip */}
            <div className="absolute inset-x-0 bottom-0 h-14"
                 style={{ background: "linear-gradient(180deg, transparent, rgba(15,23,42,0.75))" }} />
            {/* overlay animation on top of the static frame */}
            {idx === i && <sc.Overlay />}
          </motion.div>
        ))}
      </div>

      {/* Bottom caption + scene label */}
      <div className="flex items-center justify-between px-4 py-2.5 border-t border-white/5">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-[#84CC16] animate-pulse" />
          <span className="text-[10.5px] font-black uppercase tracking-[0.2em]"
                style={{ color: "#84CC16" }}
                data-testid="hero-proof-label">
            {labels[idx]}
          </span>
        </div>
        <span className="text-[11px] text-white/60 truncate" data-testid="hero-proof-caption">
          {captions[idx]}
        </span>
      </div>
    </div>
  );
}

// Iter280 · Small overlays that add live motion on top of the static
// screenshots so the loop feels alive, not slideshow-y.
function KanbanOverlay() {
  return (
    <motion.div
      className="absolute rounded px-2 py-1 text-[10px] font-black text-white"
      style={{ background: "rgba(132,204,22,0.85)", boxShadow: "0 6px 22px -6px rgba(132,204,22,0.7)",
               top: "36%", left: "18%" }}
      initial={{ x: 0, y: 0, opacity: 0.9 }}
      animate={{ x: [0, 210, 210], y: [0, 40, 40], opacity: [0.9, 1, 0.9] }}
      transition={{ duration: 2.4, times: [0, 0.6, 1], ease: "easeInOut" }}
      data-testid="hero-kanban-overlay">
      Blake · 92
    </motion.div>
  );
}
function HqOverlay() {
  return (
    <motion.span
      className="absolute rounded-full h-2.5 w-2.5"
      style={{ background: "#10B981", boxShadow: "0 0 14px #10B981",
               top: "13%", left: "5%" }}
      animate={{ scale: [1, 1.6, 1], opacity: [0.75, 1, 0.75] }}
      transition={{ duration: 1.4, repeat: Infinity }}
      data-testid="hero-hq-overlay" />
  );
}
function MobileOverlay() {
  return (
    <motion.div
      className="absolute rounded-lg px-2.5 py-1.5 text-[10px] font-black flex items-center gap-1.5"
      style={{ background: "rgba(30,41,59,0.95)", border: "1px solid rgba(132,204,22,0.55)",
               boxShadow: "0 12px 24px -8px rgba(0,0,0,0.6)", top: "22%", left: "6%", right: "6%" }}
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.35, duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
      data-testid="hero-mobile-overlay">
      <span className="w-4 h-4 rounded flex items-center justify-center" style={{ background: "#84CC16" }}>
        <Zap className="w-2.5 h-2.5" style={{ color: "#0F172A" }} />
      </span>
      <span className="text-white leading-tight flex-1">1 HOT lead · Blake · 92</span>
    </motion.div>
  );
}


/**
 * CasesSwipeDeckMobile — mobile-only horizontal snap deck showing every
 * case study card in one flick. Renders under `md:hidden` so tablets/
 * desktops keep the tab-driven expanded panel. Each card is a dense
 * KPI hero: giant metric on top, title + body, CTA to /signup, plus
 * progress dots and a card counter (2 / 5).
 */
function CasesSwipeDeckMobile({ tabs, eyebrow, cta }) {
  const scrollerRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const w = el.clientWidth;
    const idx = Math.round(el.scrollLeft / w);
    if (idx !== activeIdx) setActiveIdx(idx);
  };

  const goTo = (i) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
  };

  return (
    <div className="md:hidden mt-8" data-testid="cases-swipe-deck">
      {/* Counter chip */}
      <div className="mb-3 flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10.5px] font-black uppercase tracking-[0.18em]"
          style={{ backgroundColor: "rgba(132,204,22,0.14)", color: "#84CC16", border: "1px solid rgba(132,204,22,0.3)" }}>
          <BarChart3 className="w-3 h-3" /> {eyebrow}
        </span>
        <span data-testid="cases-swipe-counter"
          className="font-mono-tech text-[11px] tabular-nums font-bold" style={{ color: "#A1A1AA" }}>
          {activeIdx + 1} / {tabs.length}
        </span>
      </div>
      <div
        ref={scrollerRef}
        onScroll={onScroll}
        data-testid="cases-swipe-scroller"
        className="flex overflow-x-auto snap-x snap-mandatory -mx-5 px-5 pb-4 gap-4 scroll-smooth"
        style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}>
        {tabs.map((t, i) => (
          <article
            key={t.id}
            data-testid={`cases-swipe-card-${t.id}`}
            className="rounded-2xl overflow-hidden border-l-4 shrink-0 snap-center flex flex-col"
            style={{ backgroundColor: "#1E293B", borderColor: "#84CC16", width: "calc(100vw - 40px)" }}>
            {/* KPI hero panel */}
            <div className="p-5 text-center"
              style={{ background: "linear-gradient(135deg, #0A1233 0%, #160C2D 100%)" }}>
              <div className="font-mono-tech text-[10px] uppercase tracking-[0.22em] opacity-60" style={{ color: "#FFFFFF" }}>KPI</div>
              <div data-testid={`cases-swipe-kpi-${t.id}`}
                className="font-display text-[38px] font-black mt-1.5 leading-none tracking-tight"
                style={{ color: "#E5C572" }}>{t.kpi}</div>
              <div className="mt-2 text-[10.5px] font-bold uppercase tracking-[0.16em]" style={{ color: "#84CC16" }}>
                {t.label}
              </div>
            </div>
            {/* Body */}
            <div className="p-5 flex-1 flex flex-col">
              <h3 className="font-display text-[20px] font-black tracking-tight leading-tight" style={{ color: "#FFFFFF" }}>
                {t.title}
              </h3>
              <p className="mt-3 text-[14px] leading-relaxed flex-1" style={{ color: "#A1A1AA" }}>{t.body}</p>
              <Link to="/signup"
                data-testid={`cases-swipe-cta-${t.id}`}
                className="mt-5 inline-flex items-center justify-center gap-2 font-bold text-[13px] h-11 px-4 rounded-md"
                style={{ backgroundColor: "#84CC16", color: "#0F172A", boxShadow: "0 6px 20px -8px rgba(132,204,22,0.55)" }}>
                {cta}<ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </article>
        ))}
      </div>
      {/* Progress dots */}
      <div className="mt-3 flex items-center justify-center gap-2" data-testid="cases-swipe-dots">
        {tabs.map((_t, i) => (
          <button
            key={i}
            type="button"
            aria-label={`Go to case ${i + 1}`}
            onClick={() => goTo(i)}
            data-testid={`cases-swipe-dot-${i}`}
            className="h-2 rounded-full transition-all"
            style={{
              width: i === activeIdx ? 22 : 8,
              backgroundColor: i === activeIdx ? "#84CC16" : "rgba(255,255,255,0.25)",
            }}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * TestimonialCarouselMobile — mobile-only swipeable carousel with snap +
 * progress dots. Rendered under `md:hidden` so tablets/desktops keep the
 * existing 3-col grid.
 */
function TestimonialCarouselMobile({ items }) {
  const scrollerRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const w = el.clientWidth;
    const idx = Math.round(el.scrollLeft / w);
    if (idx !== activeIdx) setActiveIdx(idx);
  };

  const goTo = (i) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
  };

  return (
    <div className="md:hidden mt-8">
      <div
        ref={scrollerRef}
        onScroll={onScroll}
        data-testid="testimonials-carousel"
        className="flex overflow-x-auto snap-x snap-mandatory -mx-5 px-5 pb-4 gap-4 scroll-smooth"
        style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}>
        {items.map((t, i) => (
          <figure
            key={t.a}
            data-testid={`testimonial-mobile-${i}`}
            className="border p-6 rounded-xl flex flex-col shrink-0 snap-center"
            style={{ backgroundColor: "#1E293B", borderColor: "rgba(132,204,22,0.18)", width: "calc(100vw - 40px)" }}>
            <div className="flex gap-0.5 mb-4" style={{ color: "#84CC16" }}>
              {[1, 2, 3, 4, 5].map((n) => <Star key={n} className="w-4 h-4" fill="#84CC16" />)}
            </div>
            <blockquote className="font-display text-[17px] font-semibold leading-snug flex-1" style={{ color: "#FFFFFF" }}>&ldquo;{t.q}&rdquo;</blockquote>
            <figcaption className="mt-5 flex items-center gap-3 pt-4 border-t" style={{ borderColor: "rgba(132,204,22,0.18)" }}>
              <img src={AVATARS[i % AVATARS.length]} alt={t.a} className="w-10 h-10 rounded-full object-cover ring-2" style={{ "--tw-ring-color": "rgba(132,204,22,0.3)" }} />
              <div className="font-display text-[13px] font-bold" style={{ color: "#FFFFFF" }}>{t.a}</div>
            </figcaption>
          </figure>
        ))}
      </div>
      {/* Progress dots */}
      <div className="mt-3 flex items-center justify-center gap-2" data-testid="testimonials-carousel-dots">
        {items.map((_t, i) => (
          <button
            key={i}
            type="button"
            aria-label={`Go to testimonial ${i + 1}`}
            onClick={() => goTo(i)}
            data-testid={`testimonials-dot-${i}`}
            className="h-2 rounded-full transition-all"
            style={{
              width: i === activeIdx ? 22 : 8,
              backgroundColor: i === activeIdx ? "#84CC16" : "rgba(255,255,255,0.25)",
            }}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * StickyMobileTrialBar — pinned bottom trial CTA for the public marketing
 * surface. Uses safe-area-inset-bottom for iOS notch phones. Hidden on
 * >=lg screens where the header CTA is already prominent.
 * `variant`: "landing" | "blog" tweaks copy and background contrast.
 */
export function StickyMobileTrialBar({ lang = "en", variant = "landing" }) {
  const label = lang === "pt" ? "Comece o FREE TRIAL"
              : lang === "es" ? "Comienza el FREE TRIAL"
              : "Start FREE TRIAL";
  const micro = lang === "pt" ? "14 dias · sem cartão"
              : lang === "es" ? "14 días · sin tarjeta"
              : "14 days · no card";
  return (
    <div
      data-testid={`sticky-trial-bar-${variant}`}
      className="lg:hidden fixed bottom-0 left-0 right-0 z-40 border-t backdrop-blur-xl"
      style={{
        background: "rgba(15,8,32,0.92)",
        borderColor: "rgba(132,204,22,0.28)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
        boxShadow: "0 -8px 24px -12px rgba(0,0,0,0.6)",
      }}>
      <div className="px-4 py-2.5 flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-display text-[13px] font-black text-white leading-tight truncate">
            {label}
          </div>
          <div className="text-[10.5px] uppercase tracking-[0.14em] font-bold" style={{ color: "#84CC16" }}>
            {micro}
          </div>
        </div>
        <Link
          to="/signup"
          data-testid={`sticky-trial-cta-${variant}`}
          className="inline-flex items-center gap-1.5 h-11 px-5 rounded-md font-extrabold uppercase tracking-tight text-[13px] whitespace-nowrap"
          style={{ backgroundColor: "#84CC16", color: "#0F172A", boxShadow: "0 6px 20px -8px rgba(132,204,22,0.6)" }}>
          Trial <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}


/**
 * GeoHintBanner (Iter320).
 *
 * Small dismissable strip anchored under the sticky nav that surfaces the
 * IP-based language flip. Copy is written in the *new* language (PT or ES)
 * so the visitor sees confirmation in the language they just landed on,
 * plus a one-tap escape hatch back to EN.
 *
 * Country name is looked up from a small ISO-3166 mini-map — we only ship
 * the countries our /geo/lang classifier maps to PT/ES, so any hit we get
 * here is guaranteed to be in this table.
 */
const _COUNTRY_NAMES = {
  pt: {
    BR: { pt: "Brasil",           en: "Brazil",            es: "Brasil" },
    PT: { pt: "Portugal",         en: "Portugal",          es: "Portugal" },
    AO: { pt: "Angola",           en: "Angola",            es: "Angola" },
    MZ: { pt: "Moçambique",       en: "Mozambique",        es: "Mozambique" },
    CV: { pt: "Cabo Verde",       en: "Cape Verde",        es: "Cabo Verde" },
    GW: { pt: "Guiné-Bissau",     en: "Guinea-Bissau",     es: "Guinea-Bisáu" },
    ST: { pt: "São Tomé",         en: "São Tomé",          es: "Santo Tomé" },
    TL: { pt: "Timor-Leste",      en: "Timor-Leste",       es: "Timor Oriental" },
  },
  es: {
    ES: { pt: "Espanha",          en: "Spain",             es: "España" },
    MX: { pt: "México",           en: "Mexico",            es: "México" },
    AR: { pt: "Argentina",        en: "Argentina",         es: "Argentina" },
    CO: { pt: "Colômbia",         en: "Colombia",          es: "Colombia" },
    PE: { pt: "Peru",             en: "Peru",              es: "Perú" },
    VE: { pt: "Venezuela",        en: "Venezuela",         es: "Venezuela" },
    CL: { pt: "Chile",            en: "Chile",             es: "Chile" },
    EC: { pt: "Equador",          en: "Ecuador",           es: "Ecuador" },
    GT: { pt: "Guatemala",        en: "Guatemala",         es: "Guatemala" },
    CU: { pt: "Cuba",             en: "Cuba",              es: "Cuba" },
    BO: { pt: "Bolívia",          en: "Bolivia",           es: "Bolivia" },
    DO: { pt: "Rep. Dominicana",  en: "Dominican Rep.",    es: "Rep. Dominicana" },
    HN: { pt: "Honduras",         en: "Honduras",          es: "Honduras" },
    PY: { pt: "Paraguai",         en: "Paraguay",          es: "Paraguay" },
    SV: { pt: "El Salvador",      en: "El Salvador",       es: "El Salvador" },
    NI: { pt: "Nicarágua",        en: "Nicaragua",         es: "Nicaragua" },
    CR: { pt: "Costa Rica",       en: "Costa Rica",        es: "Costa Rica" },
    PA: { pt: "Panamá",           en: "Panama",            es: "Panamá" },
    UY: { pt: "Uruguai",          en: "Uruguay",           es: "Uruguay" },
    PR: { pt: "Porto Rico",       en: "Puerto Rico",       es: "Puerto Rico" },
  },
};

// Copy is written IN the newly-selected language so the visitor sees a
// confirmation in the language they just switched into. `revert_cta` is
// however written in the ORIGINAL language (EN by default) so it also
// makes sense to someone who doesn't read PT/ES.
const _GEO_HINT_COPY = {
  pt: {
    prefix: "Detectamos que você está no",
    suffix: "trocamos o site para Português.",
    keep_cta: "Manter",
    revert_cta: "Prefer English",
  },
  es: {
    prefix: "Detectamos que estás en",
    suffix: "cambiamos el sitio a Español.",
    keep_cta: "Mantener",
    revert_cta: "Prefer English",
  },
};

function GeoHintBanner({ hint, onKeep, onRevert }) {
  const copy = _GEO_HINT_COPY[hint.targetLang] || _GEO_HINT_COPY.pt;
  const countryDict = (_COUNTRY_NAMES[hint.targetLang] || {})[hint.country];
  const countryName = countryDict?.[hint.targetLang] || hint.country;
  return (
    <div data-testid="landing-geo-hint"
         className="w-full border-b"
         style={{ background: "linear-gradient(90deg, rgba(132,204,22,0.14) 0%, rgba(59,130,246,0.14) 100%)",
                  borderColor: "rgba(132,204,22,0.35)" }}>
      <div className="max-w-7xl mx-auto px-3 sm:px-5 lg:px-10 py-2.5 flex items-center gap-2 sm:gap-3 flex-wrap">
        <span className="text-[16px] leading-none" aria-hidden="true">🌎</span>
        <p className="text-[12.5px] sm:text-[13px] text-white flex-1 min-w-0">
          <span className="opacity-80">{copy.prefix}</span>
          <b className="mx-1 text-[#84CC16]">{countryName}</b>
          <span className="opacity-80">— {copy.suffix}</span>
        </p>
        <button type="button" onClick={onRevert}
                data-testid="landing-geo-hint-revert"
                className="h-7 px-2.5 rounded-md text-[11px] font-bold uppercase tracking-widest text-white/90 border border-white/25 hover:border-white hover:bg-white/10 transition-colors">
          {copy.revert_cta}
        </button>
        <button type="button" onClick={onKeep}
                data-testid="landing-geo-hint-keep"
                aria-label={copy.keep_cta}
                className="w-7 h-7 rounded-md flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-colors">
          <XIcon className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

